/**
 * Thrown projectiles (bazooka shell, grenade, sandbag). Fixed-point PFP, gravity and wind every tick, and
 * every crossed cell tested so that a fast projectile cannot tunnel through thin terrain. What is thrown by hand
 * (Weapon.lob) flies in a higher arc than the shell (THROW_GRAVITY).
 */
import { Physique, STATE_LIQUID, BLAST_RIM, destroyCircle, isqrt, wakeRect } from '@invaders/sim';
import { PFP, PFP_SHIFT, type GameState, type Projectile } from './state';
import { explode } from './explosion';
import { damageUnit, ownerPlayer } from './unit';

const bounces = (w: number) => w === WEAPON_GRENADE || w === WEAPON_MINE || w === WEAPON_CRYO || w === WEAPON_ICE_MINE || w === WEAPON_BATTERY;
/** Thrown things that become an object where they come to rest: the mines and the battery. */
const isMine = (w: number) => w === WEAPON_MINE || w === WEAPON_ICE_MINE || w === WEAPON_BATTERY;
/** Thrown barrels: they fly like a shell and start rolling where they land. */
const isBarrel = (w: number) => w === WEAPON_ACID_BARREL || w === WEAPON_POWDER_BARREL;
import { WEAPONS, BULLET_SPEED, PISTOL_RECOIL, unitAt } from './weapons';
import { updateRay } from './arsenal';
import { placeBattery } from './battery';
import { barrelAt, hitBarrel, landBarrel } from './barrel';
import { landRoomba, roombaAt } from './roomba';
import { WEAPON_ROOMBA, WEAPON_ACID_BARREL, WEAPON_POWDER_BARREL, PROJ_ACID_DROP, WEAPON_BATTERY, PROJ_LAVA_BOMB, WEAPON_PISTOL, WEAPON_LASER, WEAPON_COCKTAIL, WEAPON_CRYO, WEAPON_DOLL, WEAPON_GRENADE, WEAPON_ICE_MINE, WEAPON_METEOR, WEAPON_MINE, WEAPON_SANDBAG } from './weapon-ids';
import { MINE_EXPLOSIVE, MINE_ICE, detonate, mineAt, placeMine } from './mines';
import { chestAt, hitMimic, mimicAt } from './chests';
import { freezeArea } from './cold';
import { FP, FP_SHIFT, type Unit } from './state';
import { UNIT_H, UNIT_W } from './unit';

const LIQUID = STATE_LIQUID;

/** 12/256 cell per tick², as in the prototype: a full-power shot at 45° crosses the map. */
export const PROJECTILE_GRAVITY = 12 << (PFP_SHIFT - 8);
/** A weapon thrown by hand (Weapon.lob) falls twice as hard: a clear arc (author's rule; see MAX_THROW_SPEED). */
export const THROW_GRAVITY = 2 * PROJECTILE_GRAVITY;
/** Acceleration per point of wind at full sensitivity: at wind 10, a long shot drifts by about 140 cells. */
export const WIND_ACCEL = 32;
/** Volcano's lava bombs (maps/events.ts): splash radius, damage on a direct hit, wind sensitivity (heavy: 1/4). */
export const LAVA_BOMB_RADIUS = 2;
export const LAVA_BOMB_DAMAGE = 10;
const LAVA_BOMB_WIND = 1;
/** A drop of an acid barrel's burst: its splash, and the acid burn of the unit it hits. */
export const ACID_DROP_RADIUS = 3;
export const ACID_DROP_DAMAGE = 20;
/** The owner cannot be hit during the first ticks of the flight. */
const OWNER_GRACE_TICKS = 8;
/** GDD: the grenade explodes 3 s after the throw. */
export const GRENADE_FUSE_TICKS = 180;
/** On a bounce, the speed across the surface is halved... */
const BOUNCE_NUM = 1;
const BOUNCE_DEN = 2;
/** ...and the speed along the ground loses a quarter (rolling friction). */
const FRICTION_NUM = 3;
const FRICTION_DEN = 4;
/** Below this vertical speed a grenade stops bouncing and lies on the ground. */
const REST_SPEED = 64 << (PFP_SHIFT - 8);

/**
 * The unit a projectile goes through: its owner during the first ticks of the flight, and as long as it is still inside
 * it (a slow throw takes longer to leave the thrower); -1 otherwise.
 */
function ignored(state: GameState, p: Projectile, x: number, y: number): number {
  if (p.age < OWNER_GRACE_TICKS) return p.owner;
  const u = p.owner >= 0 ? state.units[p.owner] : undefined;
  if (!u || !u.alive) return -1;
  const ux = u.x >> FP_SHIFT;
  const uy = u.y >> FP_SHIFT;
  return x >= ux && x < ux + UNIT_W && y >= uy && y < uy + UNIT_H ? p.owner : -1;
}

function blocked(state: GameState, cx: number, cy: number, ignore: number): boolean {
  if (cy < 0) return false;
  const { w, data } = state.world.physique;
  if (state.world.rules.materials.state[data[cy * w + cx]] < LIQUID || unitAt(state, cx, cy, ignore) !== null || mimicAt(state, cx, cy)) return true;
  // A barrel or a Roomba stops a shot, except one still leaving the thrower (`ignore` is the thrower) or the doll (-2).
  return ignore === -1 && (barrelAt(state, cx, cy) >= 0 || roombaAt(state, cx, cy) >= 0);
}

function inLiquid(state: GameState, p: Projectile): boolean {
  const { w, h, data } = state.world.physique;
  const cx = p.x >> PFP_SHIFT;
  const cy = p.y >> PFP_SHIFT;
  return cx >= 0 && cx < w && cy >= 0 && cy < h && state.world.rules.materials.state[data[cy * w + cx]] === LIQUID;
}

/** Gravity on a projectile of `weapon` per tick (fixed-point PFP): a throw falls harder, the Moon divides it. */
export function projectileGravity(state: GameState, weapon: number): number {
  const lob = weapon !== PROJ_LAVA_BOMB && WEAPONS[weapon]?.lob === true;
  return Math.trunc((lob ? THROW_GRAVITY : PROJECTILE_GRAVITY) / state.map.gravityDiv);
}

function accelerate(state: GameState, p: Projectile): void {
  const wind = p.weapon === PROJ_LAVA_BOMB || p.weapon === PROJ_ACID_DROP ? LAVA_BOMB_WIND : WEAPONS[p.weapon].wind;
  p.vy += projectileGravity(state, p.weapon);
  p.vx += (state.wind * WIND_ACCEL * wind) >> 2;
  if (bounces(p.weapon) && inLiquid(state, p)) {
    // GDD: the grenade is slowed down in liquids.
    p.vx = Math.trunc((p.vx * 7) / 8);
    p.vy = Math.trunc((p.vy * 7) / 8);
  }
}

/** Sandbag impact: a disc of `mat` (sand) in every free or liquid cell, never inside a unit. */
export function dropSand(state: GameState, cx: number, cy: number, radius: number, mat: number = Physique.SAND): void {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  const r2 = radius * radius;
  for (let y = Math.max(0, cy - radius); y <= Math.min(h - 1, cy + radius); y++) {
    for (let x = Math.max(0, cx - radius); x <= Math.min(w - 1, cx + radius); x++) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy > r2 || matState[data[y * w + x]] < LIQUID || unitAt(state, x, y, -1)) continue;
      data[y * w + x] = mat;
      state.world.cellState.data[y * w + x] = 0;
    }
  }
  wakeRect(state.world, cx - radius, cy - radius, cx + radius, cy + radius);
}

/** One cell of oil in two in the disc turns into a flame: each drop of the splash has one next to it. */
function lightSplash(state: GameState, cx: number, cy: number, radius: number): void {
  const { w, h, data } = state.world.physique;
  const cells = state.world.cellState.data;
  for (let y = Math.max(0, cy - radius); y <= Math.min(h - 1, cy + radius); y++) {
    for (let x = Math.max(0, cx - radius); x <= Math.min(w - 1, cx + radius); x++) {
      const i = y * w + x;
      if (data[i] === Physique.OIL && ((x + y) & 1) === 0) { data[i] = Physique.FIRE; cells[i] = 0; }
    }
  }
}

/** The voodoo doll's flight is over: the linked unit is on its own again (it keeps its speed). */
function releasePuppet(state: GameState, p: Projectile): void {
  const u = p.link >= 0 ? state.units[p.link] : undefined;
  if (u) u.puppet = false;
  p.link = -1;
}

/**
 * GDD: while the doll flies, the linked enemy copies its trajectory exactly (same speed, gravity and wind)
 * from its own position. Collisions apply: a wall stops it (and breaks the link), falls hurt as usual.
 */
function driveLinked(state: GameState, p: Projectile): void {
  if (p.link < 0) return;
  const u = state.units[p.link];
  if (!u || !u.alive || (u.puppet === false && p.age > 0)) { p.link = -1; return; }
  u.puppet = true;
  u.vx = Math.trunc(p.vx / (PFP / 256));
  u.vy = Math.trunc(p.vy / (PFP / 256));
  u.walkDir = 0;
}

/** Impact of a shell or a sandbag at cell (cx, cy), the last free cell before the obstacle. */
function impact(state: GameState, p: Projectile, cx: number, cy: number, hitX: number, hitY: number): void {
  if (p.weapon === PROJ_LAVA_BOMB) {
    // A lava bomb of the volcano: a splash of lava, and a burn for the unit it hits.
    const target = unitAt(state, hitX, hitY, -1);
    if (target) damageUnit(state, target, LAVA_BOMB_DAMAGE, 'lava');
    dropSand(state, cx, cy, LAVA_BOMB_RADIUS, Physique.LAVA);
    return;
  }
  if (p.weapon === PROJ_ACID_DROP) {
    const target = unitAt(state, hitX, hitY, -1);
    if (target) damageUnit(state, target, ACID_DROP_DAMAGE, 'acid');
    dropSand(state, cx, cy, ACID_DROP_RADIUS, Physique.ACID);
    return;
  }
  const weapon = WEAPONS[p.weapon];
  if (isBarrel(p.weapon)) { landBarrel(state, p.weapon, cx, cy, p.vx, p.owner); return; }
  if (p.weapon === WEAPON_ROOMBA) { landRoomba(state, cx, cy, p.vx, p.owner); return; }
  if (p.weapon === WEAPON_DOLL) { releasePuppet(state, p); return; } // the doll itself is harmless
  hitMimic(state, hitX, hitY); // a projectile destroys the mimic it hits (an explosion would anyway)
  if (p.weapon === WEAPON_COCKTAIL) {
    // The bottle breaks: a splash of oil, on fire all through. Flames put on top of it would rise while the oil falls,
    // and most of it would never catch.
    dropSand(state, cx, cy, weapon.radius, Physique.OIL);
    lightSplash(state, cx, cy, weapon.radius);
    state.events.push({ type: 'explosion', x: cx, y: cy, radius: weapon.radius });
    return;
  }
  if (p.weapon === WEAPON_SANDBAG) {
    const target = unitAt(state, hitX, hitY, -1);
    if (target) damageUnit(state, target, weapon.damage);
    dropSand(state, cx, cy, weapon.radius);
    state.events.push({ type: 'explosion', x: cx, y: cy, radius: 0 });
    return;
  }
  explode(state, hitX, hitY, weapon.radius, weapon.damage, weapon.knock);
}

export function updateProjectiles(state: GameState): void {
  const { w, h } = state.world.physique;
  for (let i = state.projectiles.length - 1; i >= 0; i--) {
    const p = state.projectiles[i];
    // Its blows are its owner's team's (friendly fire).
    state.source = ownerPlayer(state, p.owner);
    const done = p.weapon === WEAPON_METEOR ? updateMeteor(state, p)
      : p.weapon === WEAPON_PISTOL ? updateBullet(state, p, w, h)
      : p.weapon === WEAPON_LASER ? updateRay(state, p)
      : bounces(p.weapon) ? updateGrenade(state, p) : updateShell(state, p, w, h);
    state.source = -1;
    if (done) state.projectiles.splice(i, 1);
  }
}

/**
 * The pistol's bullet: a straight line, every crossed cell tested, until it sets off a mine, destroys a mimic, hits a
 * unit (15 damage and a blow) or the ground (it crumbles a little of it), or leaves the map. Returns true once gone.
 */
function updateBullet(state: GameState, p: Projectile, w: number, h: number): boolean {
  const { data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  const weapon = WEAPONS[WEAPON_PISTOL];
  const x0 = p.x >> PFP_SHIFT;
  const y0 = p.y >> PFP_SHIFT;
  const nx = p.x + p.vx;
  const ny = p.y + p.vy;
  const dxc = (nx >> PFP_SHIFT) - x0;
  const dyc = (ny >> PFP_SHIFT) - y0;
  const n = Math.max(Math.abs(dxc), Math.abs(dyc), 1);
  for (let s = 1; s <= n; s++) {
    const cx = x0 + Math.trunc((dxc * s) / n);
    const cy = y0 + Math.trunc((dyc * s) / n);
    // No gravity: out of the map, even by the top, it never comes back.
    if (cx < 0 || cx >= w || cy < 0 || cy >= h) return true;
    const end = () => { state.events.push({ type: 'shot', x0, y0, x1: cx, y1: cy }); return true; };
    const mine = mineAt(state, cx, cy);
    if (mine >= 0) { detonate(state, mine); return end(); } // GDD: the bullet sets off a mine from afar
    if (hitMimic(state, cx, cy) || hitBarrel(state, cx, cy) || roombaAt(state, cx, cy) >= 0) return end();
    const target = unitAt(state, cx, cy, p.owner);
    if (target) {
      damageUnit(state, target, weapon.damage, 'direct');
      target.vx += Math.trunc((PISTOL_RECOIL * p.vx) / (BULLET_SPEED * PFP));
      target.vy -= FP >> 2;
      target.walkAcc = 0;
      return end();
    }
    if (matState[data[cy * w + cx]] < LIQUID) { destroyCircle(state.world, cx, cy, weapon.radius); return end(); }
  }
  p.x = nx;
  p.y = ny;
  p.age++;
  return false;
}

/** Shell or sandbag: flies until it hits a solid, a powder or a unit. Returns true once gone. */
function updateShell(state: GameState, p: Projectile, w: number, h: number): boolean {
  accelerate(state, p);
  if (p.weapon === WEAPON_DOLL) driveLinked(state, p);
  const nx = p.x + p.vx;
  const ny = p.y + p.vy;
  const x0 = p.x >> PFP_SHIFT;
  const y0 = p.y >> PFP_SHIFT;
  const dxc = (nx >> PFP_SHIFT) - x0;
  const dyc = (ny >> PFP_SHIFT) - y0;
  const n = Math.max(Math.abs(dxc), Math.abs(dyc));
  const ignore = ignored(state, p, x0, y0);
  let px = x0;
  let py = y0;
  for (let s = 1; s <= n; s++) {
    const cx = x0 + Math.trunc((dxc * s) / n);
    const cy = y0 + Math.trunc((dyc * s) / n);
    if (cx < 0 || cx >= w || cy >= h) { if (p.weapon === WEAPON_DOLL) releasePuppet(state, p); return true; }
    // The doll flies through units (it is harmless), its linked enemy too.
    if (blocked(state, cx, cy, p.weapon === WEAPON_DOLL ? -2 : ignore) && !(p.weapon === WEAPON_DOLL && unitAt(state, cx, cy, -1))) {
      impact(state, p, px, py, cx, cy);
      return true;
    }
    px = cx;
    py = cy;
  }
  p.x = nx;
  p.y = ny;
  p.age++;
  return false;
}

/**
 * Grenade: bounces off solids, powders and units, losing speed each time, and explodes when its fuse runs out.
 * A thrown mine moves the same way, without a fuse, and becomes a mine where it comes to rest.
 */
function updateGrenade(state: GameState, p: Projectile): boolean {
  const { w, h } = state.world.physique;
  if (isMine(p.weapon) && p.vy === 0 && Math.abs(p.vx) < REST_SPEED && blocked(state, p.x >> PFP_SHIFT, (p.y >> PFP_SHIFT) + 1, -1)) {
    if (p.weapon === WEAPON_BATTERY) placeBattery(state, p.x >> PFP_SHIFT, p.y >> PFP_SHIFT, p.owner);
    else placeMine(state, p.x >> PFP_SHIFT, p.y >> PFP_SHIFT, p.owner, p.weapon === WEAPON_ICE_MINE ? MINE_ICE : MINE_EXPLOSIVE);
    return true;
  }
  if ((p.weapon === WEAPON_GRENADE || p.weapon === WEAPON_CRYO) && p.age >= GRENADE_FUSE_TICKS) {
    const weapon = WEAPONS[p.weapon];
    if (p.weapon === WEAPON_CRYO) freezeArea(state, p.x >> PFP_SHIFT, p.y >> PFP_SHIFT, weapon.radius, weapon.damage);
    else explode(state, p.x >> PFP_SHIFT, p.y >> PFP_SHIFT, weapon.radius, weapon.damage, weapon.knock);
    return true;
  }
  accelerate(state, p);
  const nx = p.x + p.vx;
  const ny = p.y + p.vy;
  const x0 = p.x >> PFP_SHIFT;
  const y0 = p.y >> PFP_SHIFT;
  const dxc = (nx >> PFP_SHIFT) - x0;
  const dyc = (ny >> PFP_SHIFT) - y0;
  const n = Math.max(Math.abs(dxc), Math.abs(dyc));
  const ignore = ignored(state, p, x0, y0);
  p.age++;

  let px = x0;
  let py = y0;
  for (let s = 1; s <= n; s++) {
    const cx = x0 + Math.trunc((dxc * s) / n);
    const cy = y0 + Math.trunc((dyc * s) / n);
    if (cx < 0 || cx >= w || cy >= h) return true;
    if (blocked(state, cx, cy, ignore)) {
      // Which side was hit: a wall reflects vx, a floor or ceiling vy, a corner both.
      const hitX = cx !== px && blocked(state, cx, py, ignore);
      const hitY = cy !== py && blocked(state, px, cy, ignore);
      if (hitX || !hitY) p.vx = -Math.trunc((p.vx * BOUNCE_NUM) / BOUNCE_DEN);
      if (hitY || !hitX) {
        p.vy = -Math.trunc((p.vy * BOUNCE_NUM) / BOUNCE_DEN);
        p.vx = Math.trunc((p.vx * FRICTION_NUM) / FRICTION_DEN);
        if (Math.abs(p.vy) < REST_SPEED) p.vy = 0;
      }
      // Stay in the last free cell, at its centre.
      p.x = (px << PFP_SHIFT) + (PFP >> 1);
      p.y = (py << PFP_SHIFT) + (PFP >> 1);
      return false;
    }
    px = cx;
    py = cy;
  }
  p.x = nx;
  p.y = ny;
  return false;
}


// --- ice meteor -------------------------------------------------------------------------------------

/** Side of the block of the ice meteor (GDD: 14 × 14). */
export const METEOR_SIZE = 14;
/** It falls straight down from the top of the map, fast. */
const METEOR_VY = 3 << PFP_SHIFT;
/**
 * It blows up where it lands (author's rule; the GDD's block stayed as an obstacle): an explosion of this radius with
 * the meteor's damage, then its shards line the crater's walls with METEOR_CRUST cells of ice. Values chosen.
 */
export const METEOR_BLAST_RADIUS = 20;
export const METEOR_CRUST = 2;

/** Drops a meteor on column `tx` (centre). The projectile's position is the block's top-left corner. */
export function dropMeteor(state: GameState, unit: Unit, tx: number): void {
  const w = state.world.physique.w;
  const x = Math.min(w - METEOR_SIZE, Math.max(0, tx - (METEOR_SIZE >> 1)));
  state.projectiles.push({ x: x << PFP_SHIFT, y: -METEOR_SIZE << PFP_SHIFT, vx: 0, vy: METEOR_VY, weapon: WEAPON_METEOR, owner: unit.id, age: 0, link: -1 });
}

/** The block falls until a solid, a powder, lava or a unit stops it, and blows up there (landMeteor). */
function updateMeteor(state: GameState, p: Projectile): boolean {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  const x0 = p.x >> PFP_SHIFT;
  let y = p.y >> PFP_SHIFT;
  const target = (p.y + p.vy) >> PFP_SHIFT;
  const blockedAt = (top: number) => {
    const row = top + METEOR_SIZE - 1;
    if (row >= h) return true;
    if (row < 0) return false;
    for (let x = x0; x < x0 + METEOR_SIZE; x++) {
      const m = data[row * w + x];
      if (matState[m] < STATE_LIQUID || m === Physique.LAVA) return true;
    }
    for (const u of state.units) {
      if (!u.alive) continue;
      const ux = u.x >> FP_SHIFT;
      const uy = u.y >> FP_SHIFT;
      if (ux < x0 + METEOR_SIZE && x0 < ux + UNIT_W && uy <= row && row < uy + UNIT_H) return true;
    }
    return false;
  };
  while (y < target) {
    if (blockedAt(y + 1)) { landMeteor(state, x0, y); return true; }
    y++;
  }
  p.y += p.vy;
  p.age++;
  return false;
}

/**
 * The impact: an explosion at the bottom of the block (the units under it take the full damage, a mimic is destroyed),
 * its flames put out by the cold, and a crust of ice on the walls of the crater (not in the air, nor in a unit or a chest).
 */
function landMeteor(state: GameState, x0: number, y0: number): void {
  const { w, h, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const matState = state.world.rules.materials.state;
  const r = METEOR_BLAST_RADIUS;
  const cx = x0 + (METEOR_SIZE >> 1);
  const cy = y0 + METEOR_SIZE;
  explode(state, cx, cy, r, WEAPONS[WEAPON_METEOR].damage);
  const outer = r + BLAST_RIM;
  const inner = r - METEOR_CRUST;
  const x1 = Math.max(0, cx - outer);
  const x2 = Math.min(w - 1, cx + outer);
  const y1 = Math.max(0, cy - outer);
  const y2 = Math.min(h - 1, cy + outer);
  for (let y = y1; y <= y2; y++) {
    for (let x = x1; x <= x2; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d2 = dx * dx + dy * dy;
      if (d2 > outer * outer) continue;
      const i = y * w + x;
      if (data[i] === Physique.FIRE) { data[i] = Physique.EMPTY; cells[i] = 0; }
      if (d2 < inner * inner || d2 >= r * r || data[i] !== Physique.EMPTY) continue;
      // Ice where the wall is just outside the crater, along the radius.
      const d = isqrt(d2);
      const ox = cx + Math.trunc((dx * (r + 1)) / d);
      const oy = cy + Math.trunc((dy * (r + 1)) / d);
      if (ox < 0 || ox >= w || oy < 0 || oy >= h || matState[data[oy * w + ox]] >= STATE_LIQUID) continue;
      if (unitAt(state, x, y, -1) || chestAt(state, x, y) >= 0) continue;
      data[i] = Physique.ICE;
      cells[i] = 0;
    }
  }
  wakeRect(state.world, x1, y1, x2, y2);
  state.events.push({ type: 'meteor', x: x0, y: y0 });
}
